import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Users, FileText, AlertTriangle, MessageCircle,
  Link2, Upload, Circle, Zap, Shield, ShieldAlert,
  Calendar, ArrowRight, TrendingUp, DollarSign,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/shared/PageHeader';
import { LayoutDashboard } from 'lucide-react';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/client';

const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];
const TOOLTIP_STYLE = { contentStyle: { backgroundColor: 'rgba(30,41,59,0.95)', border: 'none', borderRadius: 8, color: '#e2e8f0', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }, itemStyle: { color: '#94a3b8' }, wrapperStyle: { outline: 'none' as const }, cursor: false };

function formatSize(bytes: number) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function formatAbn(abn: string | null): string {
  if (!abn) return '-';
  const digits = abn.replace(/\D/g, '');
  if (digits.length !== 11) return abn;
  return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 11)}`;
}

function timeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

function fmtCurrency(v: number): string {
  if (v >= 0) return `$${v.toLocaleString()}`;
  return `-$${Math.abs(v).toLocaleString()}`;
}

function getTrafficLight(client: any) {
  const issues: string[] = [];
  if (!client.abn) issues.push('No ABN');
  if (parseInt(client.document_count) === 0) issues.push('No documents');
  if (parseInt(client.failed_count) > 0) issues.push(`${client.failed_count} failed`);
  if (!client.xero_org_id) issues.push('No accounting');
  if (issues.length === 0) return { color: 'green', issues };
  if (issues.length <= 2) return { color: 'yellow', issues };
  return { color: 'red', issues };
}

export default function FirmDashboardPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<string>('display_name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  // Firm dashboard data
  const { data, isLoading } = useQuery({
    queryKey: ['firm-dashboard'],
    queryFn: async () => { const r = await apiClient.get('/firm/dashboard'); return r.data.data; },
  });

  // Tax opportunities (cached separately)
  const { data: oppData } = useQuery({
    queryKey: ['opportunity-scan-summary'],
    queryFn: async () => { const r = await apiClient.get('/opportunities/scan'); return r.data.data; },
    staleTime: 10 * 60 * 1000,
  });

  // Audit risk summary
  const { data: riskData } = useQuery({
    queryKey: ['audit-risk-summary'],
    queryFn: async () => { const r = await apiClient.get('/compliance/audit-risk'); return r.data.data; },
    staleTime: 10 * 60 * 1000,
  });

  // Deadlines
  const { data: deadlineData } = useQuery({
    queryKey: ['deadlines-summary'],
    queryFn: async () => { const r = await apiClient.get('/deadlines?status=overdue&status=due_soon'); return r.data.data; },
    staleTime: 5 * 60 * 1000,
  });

  const summary = data?.summary;
  const clients = data?.clients || [];
  const recentActivity = data?.recentActivity || [];

  const avgHealthScore = clients.length > 0
    ? Math.round(clients.reduce((s: number, c: any) => s + (c.health_score || 0), 0) / clients.length)
    : 0;

  // Filter audit risk to Xero-connected clients only
  const riskClients = (riskData?.clients || []).filter((c: any) => c.xeroConnected);
  const xeroHighRisk = riskClients.filter((c: any) => c.riskLevel === 'high').length;
  const xeroMediumRisk = riskClients.filter((c: any) => c.riskLevel === 'medium').length;
  const xeroLowRisk = riskClients.filter((c: any) => c.riskLevel === 'low').length;
  const xeroAvgRisk = riskClients.length > 0 ? Math.round(riskClients.reduce((s: number, c: any) => s + c.riskScore, 0) / riskClients.length) : 0;

  // Client table filter/sort
  const filteredClients = clients
    .filter((c: any) => {
      if (filterStatus === 'xero' && !c.xero_org_id) return false;
      if (filterStatus === 'issues' && getTrafficLight(c).color === 'green') return false;
      if (!search) return true;
      return c.display_name?.toLowerCase().includes(search.toLowerCase());
    })
    .sort((a: any, b: any) => {
      let aVal = a[sortBy] || '';
      let bVal = b[sortBy] || '';
      if (['document_count', 'indexed_count', 'total_size', 'conversation_count', 'health_score'].includes(sortBy)) {
        aVal = parseInt(aVal) || 0; bVal = parseInt(bVal) || 0;
      }
      if (typeof aVal === 'string') { aVal = aVal.toLowerCase(); bVal = (bVal || '').toLowerCase(); }
      return sortDir === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });

  const handleSort = (field: string) => {
    if (sortBy === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('asc'); }
  };

  // Chart data
  const typeData = summary?.clientsByType ? Object.entries(summary.clientsByType).map(([name, value]) => ({ name, value })) : [];

  // Top opportunities
  const topOpportunities = (oppData?.opportunities || []).slice(0, 5);

  // Overdue deadlines
  const overdueDeadlines = (deadlineData?.deadlines || []).filter((d: any) => d.status === 'overdue').slice(0, 5);
  const dueSoonDeadlines = (deadlineData?.deadlines || []).filter((d: any) => d.status === 'due_soon').slice(0, 5);

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={LayoutDashboard}
        title="Firm Overview"
        description="Real-time intelligence across all clients"
      />


      {/* Row 1: KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {[
          { label: 'Clients', value: summary?.totalClients || 0 },
          { label: 'Documents', value: summary?.totalDocuments || 0 },
          { label: 'Indexed', value: summary?.totalIndexed || 0, color: 'text-green-600' },
          { label: 'Accounting', value: summary?.xeroConnected || 0, color: 'text-blue-600' },
          { label: 'Health', value: `${avgHealthScore}%`, color: avgHealthScore >= 60 ? 'text-green-600' : 'text-yellow-600' },
          { label: 'Opportunities', value: oppData?.totalOpportunities || 0, color: 'text-yellow-500' },
          { label: 'Tax Savings', value: fmtCurrency(oppData?.totalEstimatedSaving || 0), color: 'text-green-500' },
          { label: 'Overdue', value: deadlineData?.stats?.overdue || 0, color: (deadlineData?.stats?.overdue || 0) > 0 ? 'text-red-500' : '' },
        ].map((kpi) => (
          <Card key={kpi.label}>
            <CardContent className="pt-3 pb-3">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{kpi.label}</p>
              <p className={cn('text-lg font-bold', kpi.color)}>{kpi.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Row 2: Key Insights */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Top Tax Opportunities */}
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/panel/compliance/opportunities')}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              <Zap className="h-4 w-4 text-yellow-500" /> Top Tax Opportunities
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {topOpportunities.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">No opportunities found</p>
            ) : (
              <div className="divide-y">
                {topOpportunities.map((o: any, i: number) => (
                  <div key={i} className="flex items-center justify-between px-3 py-1.5">
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-medium truncate">{o.clientName}</p>
                      <p className="text-[9px] text-muted-foreground truncate">{o.title}</p>
                    </div>
                    <span className="text-[11px] font-bold text-green-500 flex-shrink-0 ml-2">{fmtCurrency(o.estimatedSaving)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="px-3 py-1.5 border-t">
              <span className="text-[10px] text-primary flex items-center gap-1">View all <ArrowRight className="h-3 w-3" /></span>
            </div>
          </CardContent>
        </Card>

        {/* Audit Risk */}
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/panel/compliance/audit-risk')}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              <ShieldAlert className="h-4 w-4 text-red-500" /> Audit Risk
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-2">
              <div className="flex gap-3">
                <div className="text-center">
                  <p className="text-lg font-bold text-red-500">{xeroHighRisk}</p>
                  <p className="text-[9px] text-muted-foreground">High</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold text-yellow-500">{xeroMediumRisk}</p>
                  <p className="text-[9px] text-muted-foreground">Medium</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold text-green-500">{xeroLowRisk}</p>
                  <p className="text-[9px] text-muted-foreground">Low</p>
                </div>
              </div>
              <div className="text-right">
                <p className={cn('text-2xl font-bold', xeroAvgRisk >= 60 ? 'text-red-500' : xeroAvgRisk >= 30 ? 'text-yellow-500' : 'text-green-500')}>
                  {xeroAvgRisk}
                </p>
                <p className="text-[9px] text-muted-foreground">Avg Score</p>
              </div>
            </div>
            <span className="text-[10px] text-primary flex items-center gap-1">View details <ArrowRight className="h-3 w-3" /></span>
          </CardContent>
        </Card>

        {/* Upcoming Deadlines */}
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/panel/deadlines')}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              <Calendar className="h-4 w-4 text-orange-500" /> Urgent Deadlines
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {overdueDeadlines.length === 0 && dueSoonDeadlines.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">No urgent deadlines</p>
            ) : (
              <div className="divide-y">
                {[...overdueDeadlines, ...dueSoonDeadlines].slice(0, 4).map((d: any, i: number) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-1.5">
                    {d.status === 'overdue' ? (
                      <AlertTriangle className="h-3 w-3 text-red-500 flex-shrink-0" />
                    ) : (
                      <Calendar className="h-3 w-3 text-yellow-500 flex-shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-medium truncate">{d.clientName}</p>
                      <p className="text-[9px] text-muted-foreground truncate">{d.title}</p>
                    </div>
                    <span className={cn('text-[10px] font-medium flex-shrink-0', d.status === 'overdue' ? 'text-red-500' : 'text-yellow-500')}>
                      {d.daysUntil < 0 ? `${Math.abs(d.daysUntil)}d overdue` : `${d.daysUntil}d`}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div className="px-3 py-1.5 border-t">
              <span className="text-[10px] text-primary flex items-center gap-1">View calendar <ArrowRight className="h-3 w-3" /></span>
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              <MessageCircle className="h-4 w-4 text-blue-500" /> Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {recentActivity.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">No recent activity</p>
            ) : (
              <div className="divide-y">
                {recentActivity.slice(0, 5).map((act: any, i: number) => (
                  <div key={i} className="flex items-start gap-2 px-3 py-1.5 hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/panel/files/clients/${act.client_id}`)}>
                    {act.type === 'chat' ? <MessageCircle className="h-3 w-3 mt-0.5 text-blue-500 flex-shrink-0" /> : <Upload className="h-3 w-3 mt-0.5 text-green-500 flex-shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-medium truncate">{act.description}</p>
                      <p className="text-[9px] text-muted-foreground">{act.client_name} · {timeAgo(act.event_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 3: Charts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {typeData.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Clients by Type</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={typeData} dataKey="value" nameKey="name" cx="35%" cy="50%" outerRadius={60} innerRadius={30} activeShape={null as any}>
                    {typeData.map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip {...TOOLTIP_STYLE} cursor={false}/>
                  <Legend layout="vertical" align="right" verticalAlign="middle" iconType="square" wrapperStyle={{ fontSize: '11px' }} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Health Score Distribution */}
        {clients.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Client Health Scores</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={[
                  { name: 'Excellent', value: clients.filter((c: any) => c.health_score >= 80).length, fill: '#22c55e' },
                  { name: 'Good', value: clients.filter((c: any) => c.health_score >= 60 && c.health_score < 80).length, fill: '#3b82f6' },
                  { name: 'Fair', value: clients.filter((c: any) => c.health_score >= 40 && c.health_score < 60).length, fill: '#f59e0b' },
                  { name: 'Poor', value: clients.filter((c: any) => (c.health_score || 0) < 40).length, fill: '#ef4444' },
                ].filter(d => d.value > 0)}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#888' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#888' }} />
                  <Tooltip {...TOOLTIP_STYLE} cursor={false}/>
                  <Bar dataKey="value" name="Clients" radius={[4, 4, 0, 0]} activeBar={false}>
                    {[
                      { fill: '#22c55e' }, { fill: '#3b82f6' }, { fill: '#f59e0b' }, { fill: '#ef4444' },
                    ].map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Opportunity Savings by Category */}
        {oppData?.byCategory && Object.keys(oppData.byCategory).length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Tax Savings by Category</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={Object.entries(oppData.byCategory).map(([name, val]: [string, any]) => ({ name: name.length > 15 ? name.substring(0, 13) + '...' : name, saving: val.saving })).sort((a: any, b: any) => b.saving - a.saving).slice(0, 5)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                  <XAxis type="number" tick={{ fontSize: 9, fill: '#888' }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 9, fill: '#999' }} width={100} />
                  <Tooltip {...TOOLTIP_STYLE} formatter={(v: number) => [fmtCurrency(v), 'Saving']} />
                  <Bar dataKey="saving" fill="#22c55e" radius={[0, 4, 4, 0]} activeBar={false} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Row 4: Client Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-sm">All Clients</CardTitle>
            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                {[
                  { key: 'all', label: 'All' },
                  { key: 'issues', label: 'Issues' },
                  { key: 'xero', label: 'Accounting' },
                ].map((f) => (
                  <button key={f.key} onClick={() => setFilterStatus(f.key)}
                    className={cn('px-2 py-0.5 text-[10px] rounded-md font-medium', filterStatus === f.key ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80')}>
                    {f.label}
                  </button>
                ))}
              </div>
              <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-48 h-7 text-xs" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-2 py-2 w-6"></th>
                  {[
                    { key: 'display_name', label: 'Client' },
                    { key: 'client_type', label: 'Type' },
                    { key: 'abn', label: 'ABN' },
                    { key: 'document_count', label: 'Docs' },
                    { key: 'xero_org_name', label: 'Accounting' },
                    { key: 'conversation_count', label: 'Chats' },
                    { key: 'health_score', label: 'Health' },
                  ].map((col) => (
                    <th key={col.key} className="px-2 py-2 text-left font-medium cursor-pointer hover:bg-muted/80 select-none" onClick={() => handleSort(col.key)}>
                      <span className="flex items-center gap-1">
                        {col.label}
                        {sortBy === col.key && <span className="text-[10px]">{sortDir === 'asc' ? '▲' : '▼'}</span>}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((client: any) => {
                  const tl = getTrafficLight(client);
                  return (
                    <tr key={client.id} className="border-b hover:bg-muted/30 cursor-pointer transition-colors" onClick={() => navigate(`/panel/files/clients/${client.id}`)}>
                      <td className="px-2 py-2">
                        <div title={tl.issues.length > 0 ? tl.issues.join(', ') : 'All good'}>
                          <Circle className={cn('h-3 w-3', tl.color === 'green' ? 'fill-green-500 text-green-500' : tl.color === 'yellow' ? 'fill-yellow-500 text-yellow-500' : 'fill-red-500 text-red-500')} />
                        </div>
                      </td>
                      <td className="px-2 py-2 font-medium">{client.display_name}</td>
                      <td className="px-2 py-2"><Badge variant="outline" className="text-[10px] capitalize">{client.client_type || '-'}</Badge></td>
                      <td className="px-2 py-2 text-muted-foreground">{formatAbn(client.abn)}</td>
                      <td className="px-2 py-2 text-center">{client.document_count}</td>
                      <td className="px-2 py-2">
                        {client.xero_org_id ? <span className="flex items-center gap-1 text-blue-600"><Link2 className="h-3 w-3" /><span className="truncate max-w-[80px]">{client.xero_org_name}</span></span> : <span className="text-muted-foreground">-</span>}
                      </td>
                      <td className="px-2 py-2 text-center">
                        {parseInt(client.conversation_count) > 0 ? <span className="flex items-center gap-0.5 justify-center"><MessageCircle className="h-3 w-3 text-muted-foreground" />{client.conversation_count}</span> : <span className="text-muted-foreground">-</span>}
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-1.5">
                          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div className={cn('h-full rounded-full', (client.health_score || 0) >= 80 ? 'bg-green-500' : (client.health_score || 0) >= 60 ? 'bg-blue-500' : (client.health_score || 0) >= 40 ? 'bg-yellow-500' : 'bg-red-400')} style={{ width: `${client.health_score || 0}%` }} />
                          </div>
                          <span className={cn('text-[10px] font-medium w-7 text-right', (client.health_score || 0) >= 80 ? 'text-green-600' : (client.health_score || 0) >= 60 ? 'text-blue-600' : (client.health_score || 0) >= 40 ? 'text-yellow-600' : 'text-red-500')}>
                            {client.health_score || 0}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-3 py-2 border-t text-[10px] text-muted-foreground">
            Showing {filteredClients.length} of {clients.length} clients
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
